import { Heart, MapPin, Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFavorite } from '@/hooks/useFavorite'
import { BEWAVE_BLUE, PLACEHOLDER_RATING } from './publicShell'
import SchoolPhotos from './SchoolPhotos'
import type { DirectorySchool } from '@/types/publicSchool'

interface SchoolCardProps {
  school: DirectorySchool
  active?: boolean
  hovered?: boolean
  onHover?: (id: string | null) => void
  onSelect?: (id: string) => void
  /**
   * Mostra o serviço ("Aula de Surf") como etiqueta. Só a vista lista liga: na
   * coluna estreita ao lado do mapa a etiqueta competiria com nome e descrição
   * — mesmo motivo pelo qual os selos saíram daqui.
   */
  showServiceTag?: boolean
}

export default function SchoolCard({
  school,
  active = false,
  hovered = false,
  onHover,
  onSelect,
  showServiceTag = false,
}: SchoolCardProps) {
  const { favorited, toggle: toggleFavorite } = useFavorite(school.slug)
  // Cidade e endereço na mesma linha. Os selos saíram do card: eles são critério
  // de FILTRO, e repetir num card de vitrine só competia com nome e descrição.
  const location = [school.city, school.address].filter(Boolean).join(' - ')

  // Só destaca no hover de mouse: no toque, mouseenter dispara junto com o tap e
  // o destaque fica preso até o próximo toque em outro card.
  const handlePointerEnter = (event: React.PointerEvent) => {
    if (event.pointerType === 'mouse') onHover?.(school.id)
  }

  const content = (
    <>
      <div className="relative">
        <SchoolPhotos
          photos={school.photos}
          alt={school.name}
          color={school.primary_color ?? '#0ea5e9'}
        />

        <button
          type="button"
          aria-label={
            favorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'
          }
          aria-pressed={favorited}
          onClick={(event) => {
            // O card é um link: sem isto, favoritar abriria a escola.
            event.preventDefault()
            event.stopPropagation()
            toggleFavorite()
          }}
          title={favorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'}
          className="absolute right-2 top-2 rounded-full p-1.5 text-white drop-shadow transition-transform hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <Heart
            aria-hidden="true"
            className={cn(
              'h-5 w-5',
              favorited ? 'fill-rose-500 stroke-rose-500' : 'fill-black/25',
            )}
          />
        </button>
      </div>

      <div className="mt-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="truncate text-sm font-semibold">{school.name}</h3>
          <span className="flex shrink-0 items-center gap-0.5 text-sm">
            <Star
              aria-hidden="true"
              className="h-3.5 w-3.5"
              style={{ fill: BEWAVE_BLUE, stroke: BEWAVE_BLUE }}
            />
            <span className="sr-only">Nota</span>
            <span>{PLACEHOLDER_RATING}</span>
          </span>
        </div>

        {location ? (
          <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{location}</span>
            {school.distance_km != null && (
              <span className="shrink-0 whitespace-nowrap">
                · {school.distance_km.toLocaleString('pt-BR')} km
              </span>
            )}
          </p>
        ) : (
          <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground/70">
            <MapPin
              aria-hidden="true"
              className="h-3.5 w-3.5 shrink-0 opacity-40"
            />
            Localização não informada
          </p>
        )}

        {school.tagline && (
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
            {school.tagline}
          </p>
        )}

        {showServiceTag && school.service_label && (
          <span className="mt-2 inline-flex w-fit rounded-full bg-primary/10 px-2.5 py-1 text-[0.6875rem] font-semibold text-primary">
            {school.service_label}
          </span>
        )}
      </div>
    </>
  )

  const className = cn(
    // Branco sobre o cinza da página: é o contraste que faz o card ser um card.
    'block w-full rounded-2xl bg-white p-3 text-left shadow-sm transition-shadow',
    // Destaque por sombra, não por contorno colorido: numa grade de cards, uma
    // borda forte briga com a foto e polui a leitura.
    active && 'shadow-lg',
    hovered && 'shadow-md',
    school.href && 'hover:shadow-md',
    // Foco de teclado: o anel só aparece via teclado (focus-visible), com o
    // mesmo peso do hover para quem navega por Tab não ficar sem feedback.
    school.href &&
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lp-blue',
  )

  // Sem href a escola não tem landing publicada — a landing responderia 404.
  // Vira um bloco não clicável em vez de um link quebrado.
  if (!school.href) {
    return (
      <div
        id={`school-${school.id}`}
        className={className}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={() => onHover?.(null)}
        onClick={() => onSelect?.(school.id)}
      >
        {content}
      </div>
    )
  }

  return (
    <a
      id={`school-${school.id}`}
      href={school.href}
      className={className}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={() => onHover?.(null)}
      onClick={() => onSelect?.(school.id)}
    >
      {content}
    </a>
  )
}
